import React, { useState } from 'react';
import type { Customer } from '../../types';
import { customersService } from '../../lib/database';

interface MealPlanNewCustomerFormProps {
  onCreated: (customer: Customer) => void;
}

/** Create a POS customer without leaving the meal plan screen. */
const MealPlanNewCustomerForm: React.FC<MealPlanNewCustomerFormProps> = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setPhone('');
    setEmail('');
    setError(null);
    setOpen(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError('Name and phone are required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await customersService.create({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        loyaltyPoints: 0,
      });
      onCreated(created);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add the customer.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-900 text-white"
      >
        Add customer
      </button>
    );
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="bg-white shadow rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-gray-900">New customer</h3>
        <button type="button" className="text-sm text-gray-500" onClick={reset}>
          Cancel
        </button>
      </div>
      <p className="text-xs text-gray-500">They are saved to Customers and can be sold a meal plan straight away.</p>
      {error && <p className="text-sm text-red-700">{error}</p>}
      <label className="block text-sm">
        Name
        <input
          className="mt-1 w-full border rounded px-3 py-2"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </label>
      <label className="block text-sm">
        Phone
        <input
          className="mt-1 w-full border rounded px-3 py-2"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          inputMode="tel"
          required
        />
      </label>
      <label className="block text-sm">
        Email <span className="text-gray-400">(optional)</span>
        <input
          type="email"
          className="mt-1 w-full border rounded px-3 py-2"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <button
        type="submit"
        disabled={busy}
        className="bg-pink-500 text-white text-sm font-semibold px-3 py-2 rounded disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save customer'}
      </button>
    </form>
  );
};

export default MealPlanNewCustomerForm;
